// src/utils/codeDesignation.ts — SCE Issues
// Décodage du code de désignation Sartorius (« Désignation de recherche »
// Windchill) des colonnes et des systèmes Hipersep, d'après « Product
// Designation code decoder Sartorius.pdf » (docs/, pages 2 et 3, nov. 2023).
// Les BioSC (page 4 et suivantes) sont volontairement laissés de côté.
//
// ─── Le principe ───
//
// Un code à positions FIXES, sans séparateur : chaque position a une largeur
// connue (1 à 4 caractères) et une liste fermée de valeurs. On découpe donc
// par largeur, jamais par motif. Un Z = « Custom » à cet endroit ; en tête
// (ZC, ZP, ZIP, ZIB), c'est le produit entier qui est Custom.
//
//   LC 1200 CO A 070 0700 SS A N 02                     colonne : 10 positions, 22 car.
//   ZIP 0PM CE Z A B A 06 C N B Z C N A A B A 15 N N    Hipersep : 21 positions, 28 car.
//
// ─── Écarts avec le document (précisions de Guillaume du 05/10) ───
//
//  - 0PL = Process L et 0PXL = Process XL existent, absents du document.
//    0PXL tient sur QUATRE caractères : tout ce qui suit est décalé d'un cran.
//  - Zone dangereuse D = Class 1 Div 1 vaut aussi pour une colonne (le document
//    ne la liste que pour les systèmes) : une seule table pour les deux.
//  - Windchill coupe parfois le code (« HIB0XLCEN ») : on décode ce qui est là,
//    le reste est compté comme manquant — un import futur pourra le compléter.
//  - Certains codes portent une espace au milieu : les blancs sont ignorés.
//  - Le NVS de l'équipement suit parfois le code (« … (NVS50365) ») : retiré
//    avant le décodage, ce n'est pas un surplus (`separerNvs`).
//
// ─── Vocabulaire de l'usine (Guillaume, 05/10) ───
//
// Base sans Z = équipement « modulaire », avec Z = « Custom » (jamais « sur
// mesure ») : `execution`, utile un jour pour des indicateurs. « Bubble trap »
// (pas « piège à bulles »), « collectes » (pas « fractions »), « fût » d'une
// colonne (pas « tube »).
//
// Pur : aucun réseau, aucun React.

export type FamilleCode = 'colonne' | 'hipersep'

export interface ChampDecode {
  libelle: string
  /** Les caractères du code à cette position. */
  code: string
  /** Sens de la valeur ; null si elle n'est pas dans la liste du document. */
  valeur: string | null
  /** Option absente (« N = Non ») : affichée en retrait. */
  absent: boolean
}

export type Execution = 'Modulaire' | 'Custom'

export interface DesignationDecodee {
  famille: FamilleCode
  /** « Colonne » / « Système Hipersep » */
  titre: string
  /** Base sans Z (LC, HIP…) = modulaire ; avec Z (ZC, ZIP…) = Custom. */
  execution: Execution
  champs: ChampDecode[]
  /** Positions absentes en fin de code (code coupé dans Windchill). */
  manquantes: number
  /** Caractères au-delà de la dernière position. */
  surplus: string
}

// ─── Lecture d'une position ───

/** Lit les caractères d'une position ; `ordre` = ses valeurs dans l'ordre du document (listes fermées). */
type Lecteur = ((code: string) => string | null) & { ordre?: readonly string[] }

const CUSTOM = 'Custom'
const NON = 'Non'

function estCustom(code: string): boolean {
  return /^Z+$/.test(code)
}

/** Liste fermée ; tout « Z » vaut « Custom » (le mot de l'usine), même s'il n'est pas listé. */
function liste(valeurs: Record<string, string>): Lecteur {
  return Object.assign((code: string) => valeurs[code] ?? (estCustom(code) ? CUSTOM : null), {
    ordre: Object.values(valeurs),
  })
}

/** Valeur chiffrée (« 0450 » → 450 mm) : tout nombre est accepté, pas seulement ceux du document. */
function nombre(format: (n: number) => string): Lecteur {
  return (code) => {
    if (/^\d+$/.test(code)) return format(Number.parseInt(code, 10))
    return estCustom(code) ? CUSTOM : null
  }
}

function pluriel(n: number, mot: string): string {
  return `${n} ${mot}${n > 1 ? 's' : ''}`
}

const OUI_NON = liste({ N: NON, A: 'Oui' })

const ZONE_DANGEREUSE = liste({
  N: NON,
  A: 'ATEX zone 2',
  B: 'ATEX zone 1',
  C: 'Class 1 Div 2',
  D: 'Class 1 Div 1',
  E: 'IECEx zone 2',
  F: 'IECEx zone 1',
})

const FILTRE = liste({ N: NON, A: 'Un', B: 'Un, avec by-pass', C: 'Deux' })

interface Position {
  libelle: string
  /** Largeur en caractères ; une fonction quand elle dépend du code (0PXL). */
  largeur: number | ((reste: string) => number)
  lire: Lecteur
}

// ─── Page 2 : colonnes ───

const POSITIONS_COLONNE: Position[] = [
  {
    libelle: 'Base',
    largeur: 2,
    lire: liste({
      LC: 'HPLC (haute pression), modulaire',
      LP: 'LPLC (basse pression), modulaire',
      PS: 'Pack & Sep, modulaire',
      ZC: 'HPLC (haute pression), Custom',
      ZP: 'LPLC (basse pression), Custom',
    }),
  },
  { libelle: 'Diamètre intérieur', largeur: 4, lire: nombre((n) => `${n} mm`) },
  { libelle: 'Norme de conception', largeur: 2, lire: liste({ CO: 'CODAP', AS: 'ASME' }) },
  { libelle: 'Zone dangereuse', largeur: 1, lire: ZONE_DANGEREUSE },
  { libelle: 'Pression de service', largeur: 3, lire: nombre((n) => `${n} bar`) },
  { libelle: 'Hauteur de fût', largeur: 4, lire: nombre((n) => `${n} mm`) },
  { libelle: 'Matériau du fût', largeur: 2, lire: liste({ SS: 'Inox', AC: 'Acrylique' }) },
  { libelle: 'Transmetteur de pression', largeur: 1, lire: OUI_NON },
  {
    libelle: 'Double enveloppe, température',
    largeur: 1,
    lire: liste({
      N: 'Sans double enveloppe, 60 °C max',
      A: 'Double enveloppe, 60 °C max',
      B: 'Sans double enveloppe, 85 °C max',
      C: 'Double enveloppe, 85 °C max',
    }),
  },
  { libelle: 'Porosité du support de lit', largeur: 2, lire: nombre((n) => `${n} µm`) },
]

// ─── Page 3 : systèmes Hipersep ───

const POSITIONS_HIPERSEP: Position[] = [
  {
    libelle: 'Base',
    largeur: 3,
    lire: liste({
      HIP: 'Hipersep, modulaire',
      HIB: 'Hipersep Bio (Resolute), modulaire',
      ZIP: 'Hipersep, Custom',
      ZIB: 'Hipersep Bio (Resolute), Custom',
    }),
  },
  {
    libelle: 'Taille',
    largeur: (reste) => (reste.startsWith('0PXL') ? 4 : 3),
    lire: liste({
      PIL: 'Pilot',
      '00S': 'S',
      '0PS': 'Process M Low Flow',
      '0PM': 'Process M',
      '0PL': 'Process L',
      '0PXL': 'Process XL',
      '00M': 'M',
      '00L': 'L',
      '0XL': 'XL',
      XXL: 'XXL',
    }),
  },
  { libelle: 'Conception', largeur: 2, lire: liste({ CE: 'CE', UL: 'UL' }) },
  { libelle: 'Zone dangereuse', largeur: 1, lire: ZONE_DANGEREUSE },
  { libelle: 'Armoire électrique', largeur: 1, lire: liste({ N: NON, A: 'Embarquée', B: 'Déportée' }) },
  {
    libelle: 'Supervision',
    largeur: 1,
    lire: liste({
      N: NON,
      A: 'Embarquée, non Ex',
      B: 'Embarquée, Ex',
      C: 'Déportée, non Ex',
      D: 'Déportée, Ex (GECMA / Stahl / P+F)',
    }),
  },
  { libelle: 'Tuyauterie', largeur: 1, lire: liste({ A: 'Inox', B: 'Polypropylène', C: 'Usage unique' }) },
  { libelle: 'Entrées de la pompe principale', largeur: 2, lire: nombre((n) => pluriel(n, 'entrée')) },
  {
    libelle: 'Gradient / ILD',
    largeur: 1,
    lire: liste({
      N: NON,
      A: '2 pompes (2 entrées pompe 2)',
      B: '2 pompes (4 entrées pompe 2)',
      C: '2 vannes de régulation',
    }),
  },
  {
    libelle: 'Bubble trap / amortisseur',
    largeur: 1,
    lire: liste({
      N: NON,
      A: 'Bubble trap seul',
      B: 'Bubble trap et amortisseur',
      C: 'Amortisseur seul',
    }),
  },
  {
    libelle: 'Injection HP',
    largeur: 1,
    lire: liste({
      N: NON,
      A: 'Pompe haute pression',
      B: 'Boucle d’injection automatique',
      C: 'Boucle Cyclojet avec pompe HP',
      D: 'Boucle + pompe HP (sans Cyclojet)',
    }),
  },
  {
    libelle: 'Échangeur thermique',
    largeur: 1,
    lire: liste({
      N: NON,
      A: 'Pré-colonne, sans by-pass',
      B: 'Pré-colonne, avec by-pass',
      D: 'Post-colonne, avec by-pass',
      H: 'Pré et post-colonne, avec by-pass',
      L: 'Jusqu’à 80 °C, pré et post-colonne, avec by-pass',
    }),
  },
  { libelle: 'Filtre', largeur: 1, lire: FILTRE },
  { libelle: 'Filtre injection HP', largeur: 1, lire: FILTRE },
  { libelle: 'Backflush / upflow', largeur: 1, lire: OUI_NON },
  { libelle: 'Pression post-colonne', largeur: 1, lire: OUI_NON },
  {
    libelle: 'pH / Cd / NIR',
    largeur: 1,
    lire: liste({
      N: NON,
      A: 'pH/Cd pré-colonne',
      B: 'pH/Cd post-colonne',
      C: 'pH/Cd pré et post-colonne',
      D: 'NIR post-colonne',
      E: 'pH/Cd pré-colonne, NIR post-colonne',
      F: 'pH/Cd et NIR post-colonne',
      G: 'pH/Cd pré et post-colonne, NIR post-colonne',
    }),
  },
  // Nitrogen flush : une entrée d'azote pour rincer les collectes.
  { libelle: 'Rinçage azote des collectes', largeur: 1, lire: OUI_NON },
  { libelle: 'Collectes', largeur: 2, lire: nombre((n) => pluriel(n, 'collecte')) },
  { libelle: 'Recyclage', largeur: 1, lire: OUI_NON },
  {
    libelle: 'Groupe hydraulique',
    largeur: 1,
    lire: liste({
      N: NON,
      A: 'Oui, avec LC600',
      B: 'Oui, avec LC800',
      C: 'Oui, avec LC1000',
      D: 'Oui, avec LC1200',
    }),
  },
]

/**
 * Les positions d'une famille de code, avec leurs valeurs dans l'ordre du
 * document (vide pour une valeur chiffrée) : les filtres de la page
 * Équipements en font un critère chacune.
 */
export function positionsDe(famille: FamilleCode): readonly { libelle: string; ordre: readonly string[] }[] {
  const positions = famille === 'colonne' ? POSITIONS_COLONNE : POSITIONS_HIPERSEP
  return positions.map((p) => ({ libelle: p.libelle, ordre: p.lire.ordre ?? [] }))
}

// ─── Décodage ───

function decouper(
  positions: readonly Position[],
  code: string,
): Pick<DesignationDecodee, 'champs' | 'manquantes' | 'surplus'> {
  const champs: ChampDecode[] = []
  let i = 0
  for (const [k, p] of positions.entries()) {
    const largeur = typeof p.largeur === 'function' ? p.largeur(code.slice(i)) : p.largeur
    const morceau = code.slice(i, i + largeur)
    // Position coupée en son milieu : comptée comme manquante, comme les suivantes.
    if (morceau.length < largeur) return { champs, manquantes: positions.length - k, surplus: '' }
    const valeur = p.lire(morceau)
    champs.push({ libelle: p.libelle, code: morceau, valeur, absent: valeur === NON })
    i += largeur
  }
  return { champs, manquantes: 0, surplus: code.slice(i) }
}

const NVS_EN_FIN = /\s*\(?\s*NVS\s*(\d+)\s*\)?\s*$/i

/**
 * Sépare le NVS qui suit parfois le code (« ZC0450… (NVS50365) ») :
 * { code: « ZC0450… », nvs: « NVS50365 » }. Sans NVS en fin : nvs vide.
 */
export function separerNvs(designation: string): { code: string; nvs: string } {
  const m = NVS_EN_FIN.exec(designation)
  if (!m || m.index === 0) return { code: designation, nvs: '' }
  return { code: designation.slice(0, m.index), nvs: `NVS${m[1] ?? ''}` }
}

/**
 * Décode une désignation de colonne ou de système Hipersep. null pour tout le
 * reste (BioSC, Booster, texte libre « Packing unit M »…) : rien à afficher.
 */
export function decoderDesignation(designation: string | undefined): DesignationDecodee | null {
  const code = separerNvs(designation ?? '').code.replace(/\s+/g, '').toUpperCase()
  const execution: Execution = code.startsWith('Z') ? 'Custom' : 'Modulaire'
  if (/^(LC|LP|PS|ZC|ZP)(\d{4}|ZZZZ)/.test(code)) {
    return { famille: 'colonne', titre: 'Colonne', execution, ...decouper(POSITIONS_COLONNE, code) }
  }
  if (/^(HIP|HIB|ZIP|ZIB)/.test(code)) {
    return { famille: 'hipersep', titre: 'Système Hipersep', execution, ...decouper(POSITIONS_HIPERSEP, code) }
  }
  return null
}
